import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { 
      name: 'Services', 
      href: '/services',
      submenu: [
        { name: 'Residential Electrical', href: '/services/residential' },
        { name: 'Commercial Electrical', href: '/services/commercial' },
        { name: 'Fire Alarm Installation & Verification', href: '/services/fire-alarm' },
        { name: 'Project Estimation & Design', href: '/services/project-estimation' },
        { name: 'Thermal Scanning', href: '/services/thermal-scanning' },
        { name: 'Generator Installation', href: '/services/generator-installation' },
        { name: 'Highrise Turnovers', href: '/services/highrise-turnovers' },
        { name: 'Panel Upgrades', href: '/services/panel-upgrades' },
        { name: 'Solar Panel Installation', href: '/services/solar' },
        { name: 'EV Charger Installation', href: '/services/ev-charging' }
      ]
    },
    { name: 'Financing', href: '/financing' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
    { name: 'Terms and Conditions', href: '/terms-and-conditions' }
  ];

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-blue-900 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:519-709-9242" className="flex items-center gap-1 hover:text-blue-200 transition-colors">
              <Phone className="h-4 w-4" />
              <span>Call 519-709-9242</span>
            </a>
            <a href="mailto:info@studeruselectric.com" className="hidden md:flex items-center gap-1 hover:text-blue-200 transition-colors">
              <Mail className="h-4 w-4" />
              <span>info@studeruselectric.com</span>
            </a>
          </div>
          <div className="text-sm">
            Proudly Serving London, Kingston & Surrounding Areas
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_se-power-solutions/artifacts/oxno8tfd_logofont.jpg" 
              alt="Studerus Electric Inc." 
              className="h-16 w-auto object-contain"
              style={{ 
                mixBlendMode: 'darken',
                filter: 'brightness(1.2) contrast(1.1)'
              }}
            />
            <div className="text-2xl font-bold text-blue-900">
              Studerus Electric Inc.
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                <Link
                  to={item.href}
                  className={`text-gray-700 hover:text-blue-900 font-medium transition-colors ${
                    location.pathname === item.href ? 'text-blue-900' : ''
                  }`}
                >
                  {item.name}
                </Link>
                {item.submenu && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-2">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button asChild className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <a href="https://calendar.app.google/mAWkTnFLgHp2TVZp6" target="_blank" rel="noopener noreferrer">Book Online</a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            {navigation.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.href}
                  className="block py-2 text-gray-700 hover:text-blue-900 font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
                {item.submenu && (
                  <div className="pl-4">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className="block py-2 text-sm text-gray-600 hover:text-blue-900"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Button asChild className="w-full mt-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <a href="https://calendar.app.google/mAWkTnFLgHp2TVZp6" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>Book Online</a>
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;